/**
 * Donut chart: overall station utilization + selected station utilization.
 * Based on ChartUserByCountry: PieChart with center label + list with LinearProgress.
 * Data: utilization (overall IN_STATION/FREE), utilizationStations (per-station).
 */
import * as React from 'react';
import PropTypes from 'prop-types';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import DonutSmallRoundedIcon from '@mui/icons-material/DonutSmallRounded';
import EvStationRoundedIcon from '@mui/icons-material/EvStationRounded';

const StyledText = styled('text', {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: { variant: 'primary' },
      style: { fontSize: theme.typography.h5.fontSize, fontWeight: theme.typography.h5.fontWeight },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: { fontSize: theme.typography.body2.fontSize, fontWeight: theme.typography.body2.fontWeight },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;
  return (
    <React.Fragment>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </React.Fragment>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

const DEFAULT_COLORS = [
  'hsl(220, 20%, 65%)',
  'hsl(220, 20%, 42%)',
  'hsl(220, 20%, 35%)',
  'hsl(220, 20%, 25%)',
];

function getSeriesByName(bar, name) {
  const list = bar?.series || [];
  const s = list.find((x) => (x.name || '').toUpperCase() === (name || '').toUpperCase());
  return s?.data && Array.isArray(s.data) ? s.data : [];
}

export default function UtilizationDonut({ utilization, utilizationStations, title = 'Station utilization' }) {
  const [selectedStationId, setSelectedStationId] = React.useState('');

  const overallInStation = React.useMemo(() => {
    const inStation = getSeriesByName(utilization, 'IN_STATION');
    const free = getSeriesByName(utilization, 'FREE');
    const total = (inStation[0] ?? 0) + (free[0] ?? 0);
    const inVal = Number(inStation[0]) || 0;
    return { inStation: inVal, total, percent: total > 0 ? Math.round((inVal / total) * 100) : 0 };
  }, [utilization]);

  const stationList = React.useMemo(() => {
    const categories = utilizationStations?.categories || [];
    const inStation = getSeriesByName(utilizationStations, 'IN_STATION');
    const free = getSeriesByName(utilizationStations, 'FREE');
    return categories.map((cat, i) => {
      const inVal = Number(inStation[i]) ?? 0;
      const freeVal = Number(free[i]) ?? 0;
      const total = inVal + freeVal;
      const percent = total > 0 ? Math.round((inVal / total) * 100) : 0;
      return { id: String(cat), label: String(cat), inStation: inVal, total, percent };
    });
  }, [utilizationStations]);

  const selectedStation = React.useMemo(
    () => stationList.find((s) => s.id === selectedStationId),
    [stationList, selectedStationId],
  );

  const pieData = React.useMemo(() => {
    const items = [];
    items.push({
      id: 'overall',
      label: 'Overall',
      value: overallInStation.inStation || 0,
      percent: overallInStation.percent,
      color: DEFAULT_COLORS[0],
    });
    if (selectedStation && selectedStation.inStation > 0) {
      items.push({
        id: 'station',
        label: `Station ${selectedStation.label}`,
        value: selectedStation.inStation,
        percent: selectedStation.percent,
        color: DEFAULT_COLORS[1],
      });
    }
    const nonZero = items.filter((d) => d.value > 0);
    if (nonZero.length === 0 && overallInStation.total >= 0) {
      nonZero.push({ id: 'overall', label: 'Overall', value: 1, percent: 0, color: DEFAULT_COLORS[0] });
    }
    return nonZero;
  }, [overallInStation, selectedStation]);

  const listItems = React.useMemo(() => {
    const items = [
      {
        key: 'overall',
        name: 'Overall',
        value: overallInStation.percent,
        color: DEFAULT_COLORS[0],
        icon: <DonutSmallRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />,
      },
    ];
    if (selectedStation) {
      items.push({
        key: 'station',
        name: `Station ${selectedStation.label}`,
        value: selectedStation.percent,
        color: DEFAULT_COLORS[1],
        icon: <EvStationRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />,
      });
    }
    return items;
  }, [overallInStation.percent, selectedStation]);

  const hasOverall = overallInStation.total > 0;
  const hasStations = stationList.length > 0;

  if (!hasOverall && !hasStations) {
    return (
      <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <CardContent>
          <Typography component="h2" variant="subtitle2">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No utilization data
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        {hasStations && (
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel id="utilization-station-label">Station</InputLabel>
            <Select
              labelId="utilization-station-label"
              value={selectedStationId}
              label="Station"
              onChange={(e) => setSelectedStationId(e.target.value)}
            >
              <MenuItem value="">— Overall only —</MenuItem>
              {stationList.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label} ({s.percent}%)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart
            colors={pieData.map((d) => d.color)}
            margin={{ left: 80, right: 80, top: 80, bottom: 80 }}
            series={[
              {
                data:
                  pieData.length > 0
                    ? pieData.map((d) => ({ id: d.id, label: d.label, value: d.value }))
                    : [{ id: 'overall', label: 'Overall', value: 1 }],
                innerRadius: 75,
                outerRadius: 100,
                paddingAngle: 0,
                highlightScope: { fade: 'global', highlight: 'item' },
              },
            ]}
            height={260}
            width={260}
            hideLegend
          >
            <PieCenterLabel
              primaryText={`${overallInStation.percent}%`}
              secondaryText="Overall"
            />
          </PieChart>
        </Box>
        {listItems.map((item) => (
          <Stack key={item.key} direction="row" sx={{ alignItems: 'center', gap: 2, pb: 2 }}>
            {item.icon}
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {item.value}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                aria-label={`Utilization ${item.name}`}
                value={Math.min(100, item.value)}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: item.color,
                  },
                }}
              />
            </Stack>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

UtilizationDonut.propTypes = {
  utilization: PropTypes.shape({
    series: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
      }),
    ),
    categories: PropTypes.arrayOf(PropTypes.string),
  }),
  utilizationStations: PropTypes.shape({
    series: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
      }),
    ),
    categories: PropTypes.arrayOf(PropTypes.string),
  }),
  title: PropTypes.string,
};
